import { useQuery } from '@tanstack/react-query'

import { axiosInstance } from '../../../helpers'
import { PipelineTab } from '../LeadsPipeline/pipelineTabs'
import { pipelineCountsKey } from './leasingSeasonKeys'
import {
    selectPipelineTabs,
    useSeasonPipelineTabs,
} from './useSeasonPipelineTabs'

export type PipelineTabCounts = Record<string, number>

export type LeadSourceCountKey = 'website' | 'email' | 'phone' | 'property'

export type LeadSourceCounts = Record<LeadSourceCountKey, number>

export const EMPTY_SOURCE_COUNTS: LeadSourceCounts = {
    website: 0,
    email: 0,
    phone: 0,
    property: 0,
}

export interface PipelineCountsResponse {
    tabs?: PipelineTab[]
    counts: PipelineTabCounts
    search_matches?: PipelineTabCounts
    source_counts?: LeadSourceCounts
    source_counts_in_leads?: LeadSourceCounts
    search_source_counts?: LeadSourceCounts
    search_source_counts_in_leads?: LeadSourceCounts
}

export function usePipelineCounts(
    seasonId: number | null | undefined,
    search?: string,
) {
    const searchTrimmed = search?.trim() || ''
    const tabsQuery = useSeasonPipelineTabs(seasonId)
    const tabs = selectPipelineTabs(tabsQuery.data)

    const countsQuery = useQuery({
        queryKey: pipelineCountsKey(seasonId, searchTrimmed),
        queryFn: async () => {
            const params: Record<string, string> = {}
            if (searchTrimmed) {
                params.search = searchTrimmed
            }
            const { data } = await axiosInstance.get<PipelineCountsResponse>(
                `/outreach/leasing-seasons/${seasonId}/pipeline-counts/`,
                { params },
            )
            return data
        },
        enabled: seasonId != null,
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        placeholderData: {
            tabs,
            counts: Object.fromEntries(tabs.map((tab) => [tab.id, 0])),
        },
    })

    const resolvedTabs = countsQuery.data?.tabs?.length
        ? countsQuery.data.tabs
        : tabs

    return {
        ...countsQuery,
        tabs: resolvedTabs,
    }
}
